<!-- Pending external uploads — always visible even when the folder is collapsed -->
@for (pending of pendingExternalUploads$ | async; track pending.resourceId) {
<div
  class="flex items-center gap-3 p-3 mt-2 border border-yellow-400 rounded-lg"
>
  <span
    class="daisy-loading daisy-loading-spinner daisy-loading-sm text-warning flex-shrink-0"
  ></span>
  <div class="flex-1 min-w-0">
    <p class="text-sm font-semibold truncate">{{ pending.name }}</p>
    <p class="text-xs text-gray-400 mt-0.5">
      @if (pending.source === 'google-drive') {
      <i class="fab fa-google-drive mr-1"></i>Google Drive } @else {
      <i class="fas fa-link mr-1"></i>URL Download } &bull; Processing&hellip;
    </p>
  </div>
  <span class="daisy-badge daisy-badge-warning daisy-badge-sm flex-shrink-0"
    >Processing</span
  >
</div>
}

<div
  class="daisy-collapse daisy-collapse-arrow bg-base-100 border border-base-300 mt-4 shadow-lg rounded-lg transition-all duration-300"
  [class.border-warning]="!isAccessible"
>
  <input type="checkbox" class="daisy-collapse-toggle" />
  <div
    class="daisy-collapse-title p-4 sm:p-6 pr-10 sm:pr-12 flex flex-row items-center group/top"
  >
    <!-- Folder Icon with Status Indicator -->
    <div class="flex items-center shrink-0">
      <div
        class="w-3 h-3 rounded-full mr-2 sm:mr-3 shrink-0"
        [class.bg-success]="isAccessible"
        [class.bg-warning]="!isAccessible"
        [title]="isAccessible ? 'Accessible Study Material' : 'Restricted Study Material'"
      ></div>
      <i class="fas fa-folder text-warning text-xl sm:text-2xl mr-3 sm:mr-4"></i>
    </div>
    <div class="flex-1 min-w-0">
      <div class="flex flex-wrap items-center gap-2 mb-1.5">
        <h2 class="text-base sm:text-xl font-bold text-base-content break-words">
          {{ studyMaterial?.name }}
        </h2>
        @if (!isAccessible) {
          @if (resourceAccess.isRestrictedBySinglePass) {
            <span class="daisy-badge daisy-badge-warning daisy-badge-sm font-semibold gap-1">
              <i class="fa-solid fa-lock text-xs"></i>
              Single Pass Required
            </span>
          } @else if (resourceAccess.isPending) {
            <span class="daisy-badge daisy-badge-warning daisy-badge-sm font-semibold gap-1">
              <i class="fa-solid fa-clock text-xs"></i>
              Verification Pending
            </span>
          } @else {
            <span class="daisy-badge daisy-badge-ghost daisy-badge-sm font-semibold gap-1 border-warning/40 text-warning">
              <i class="fa-solid fa-lock text-xs"></i>
              Subscription Required
            </span>
          }
        }
      </div>
      @if (studyMaterial?.sessionDate) {
      <p class="text-xs sm:text-sm text-base-content/60 mb-1">
        <i class="fas fa-calendar-alt mr-1"></i>
        {{ studyMaterial?.sessionDate | date: 'mediumDate' }}
      </p>
      } @else {
      <p class="text-xs sm:text-sm text-base-content/60 mb-1">
        <i class="fas fa-layer-group mr-1"></i>
        All Sessions
      </p>
      }
      <!-- Total Card Contents -->
      <p class="text-xs sm:text-sm text-base-content/60 mt-1 sm:mt-2">
        Total Contents: {{ studyMaterial?.contents?.length ?? 0 }}
      </p>
    </div>
  </div>
  <div class="daisy-collapse-content">
    @if (!isAccessible) {
      <div class="daisy-alert bg-warning/10 border border-warning/30 text-base-content text-xs p-3 sm:p-4 flex items-center gap-3 my-2 rounded-lg">
        <i class="fa-solid fa-lock text-warning text-lg shrink-0"></i>
        <div class="flex-1 min-w-0">
          <div class="font-bold text-sm">Folder Content Locked</div>
          <div class="text-base-content/70 mt-0.5 leading-relaxed">
            Access to this session's study materials is reserved for enrolled students with an active subscription or session pass.
          </div>
        </div>
      </div>
    } @else {
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4 pt-2">
      @if(studyMaterial){ @for(item of studyMaterial.contents; track
      item.resource._id){ @if (!hideUnCompletedItem(item) &&
      !isPendingExternalUpload(item.resource._id)) {
      <div
        class="p-3 sm:p-4 bg-base-200/40 border border-base-300 rounded-lg shadow-sm flex flex-col items-center justify-center text-center group relative min-w-0"
        [class.border-base-300]="item.resource.uploadStatus === 'COMPLETED'"
        [class.border-warning]="
          item.resource.uploadStatus === 'PENDING_UPLOAD' ||
          item.resource.uploadStatus === 'UPLOADING' ||
          item.resource.uploadStatus === 'PROCESSING'
        "
        [class.border-error]="item.resource.uploadStatus === 'FAILED'"
      >
        @if (editModeEnabled) {
        <div
          class="absolute top-2 right-2 w-fit h-fit hidden group-hover:block"
        >
          <button
            class="daisy-btn daisy-btn-error daisy-btn-sm daisy-btn-circle daisy-btn-outline"
            title="Delete"
            (click)="removeContent(item.resource._id)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        }
        <!-- Status indicator -->
        @if (item.resource.uploadStatus === 'PENDING_UPLOAD') {
        <span
          class="daisy-loading daisy-loading-spinner daisy-loading-sm mb-2 text-info"
        ></span>
        <span class="daisy-badge daisy-badge-info daisy-badge-sm mb-2"
          >PENDING</span
        >
        } @else if (item.resource.uploadStatus === 'UPLOADING' ||
        item.resource.uploadStatus === 'PROCESSING') {
        <span
          class="daisy-loading daisy-loading-spinner daisy-loading-sm mb-2 text-warning"
        ></span>
        <span class="daisy-badge daisy-badge-warning daisy-badge-sm mb-2">{{
          item.resource.uploadStatus
        }}</span>
        } @else if (item.resource.uploadStatus === 'FAILED') {
        <i class="fas fa-exclamation-triangle text-error text-3xl mb-2"></i>
        <span class="daisy-badge daisy-badge-error daisy-badge-sm mb-2"
          >FAILED</span
        >
        } @else {
        <i
          class="fas text-primary text-3xl mb-2"
          [class]="getIconClass(item.resource.type)"
        ></i>
        }
        <a
          (click)="openFile(item.resource)"
          class="text-sm font-medium break-all leading-tight"
          [class.text-primary]="item.resource.uploadStatus === 'COMPLETED'"
          [class.hover:underline]="item.resource.uploadStatus === 'COMPLETED'"
          [class.text-base-content/50]="item.resource.uploadStatus !== 'COMPLETED'"
          [class.pointer-events-none]="
            item.resource.uploadStatus !== 'COMPLETED'
          "
          >{{ item.name || '—' }}</a
        >
      </div>
      } } } @for (item of uploadingItems$ | async; track item) { @if
      (item.uploadSessionId !== null) {
      <black-file-upload
        progressType="radial"
        [uploadSession]="getUploadSession(item.uploadSessionId) ?? null"
      ></black-file-upload>
      } }

      <!-- Processing cards for Google Drive / URL uploads (name captured client-side, always correct) -->
      @for (pending of pendingExternalUploads$ | async; track
      pending.resourceId) {
      <div
        class="p-4 bg-base-200/40 border border-warning rounded-lg shadow-sm flex flex-col items-center justify-center text-center"
      >
        <span
          class="daisy-loading daisy-loading-spinner daisy-loading-md text-warning mb-2"
        ></span>
        @if (pending.source === 'google-drive') {
        <i class="fab fa-google-drive text-primary mb-1"></i>
        } @else {
        <i class="fas fa-link text-primary mb-1"></i>
        }
        <p class="text-sm font-medium break-all leading-tight mt-1">
          {{ pending.name }}
        </p>
        <p class="text-xs text-base-content/60 mt-0.5">
          @if (pending.source === 'google-drive') { Google Drive } @else { URL
          Download }
        </p>
        <span class="daisy-badge daisy-badge-warning daisy-badge-sm mt-2"
          >Processing</span
        >
      </div>
      } @if (editModeEnabled) {
      <black-ui-modal>
        <div
          *blackModalTrigger="let toggle = toggle"
          (click)="toggle()"
          class="p-4 h-full border-dashed border-2 border-base-300 rounded-lg shadow-sm flex flex-col items-center cursor-pointer hover:bg-base-200"
        >
          <i class="fas fa-plus text-base-content/40 text-3xl mb-2"></i>
          <p class="text-sm text-base-content/60">Add New Item</p>
        </div>
        <div
          *blackModalContent="let close = close"
          class="reveal-modal"
          (click)="close()"
        >
          <div
            class="reveal-modal-body bg-base-100 text-base-content border border-base-300 rounded-2xl shadow-2xl max-w-xl w-full"
            id="addTopicModel"
            (click)="$event.stopPropagation()"
          >
            <div class="max-w-full mx-auto px-4 py-4">
              <h5 class="font-bold">Add New Item</h5>
              <div class="daisy-divider"></div>

              <!-- Mode switcher tabs -->
              <div class="daisy-tabs daisy-tabs-boxed mb-4">
                <a
                  class="daisy-tab"
                  [class.daisy-tab-active]="activeTab === 'file'"
                  (click)="switchTab('file')"
                  >Add a File</a
                >
                <a
                  class="daisy-tab"
                  [class.daisy-tab-active]="activeTab === 'external-url'"
                  (click)="switchTab('external-url')"
                  >Add a URL</a
                >
              </div>

              <!-- Add a File tab -->
              @if (activeTab === 'file') {
              <!-- Sub-mode picker -->
              @if (fileSubMode === null) {
              <div class="flex flex-col gap-3 mt-2">
                <button
                  type="button"
                  class="daisy-btn daisy-btn-outline daisy-btn-block justify-start gap-3"
                  (click)="selectFileSubMode('device')"
                >
                  <i class="fas fa-upload"></i> Device Upload
                </button>
                <button
                  type="button"
                  class="daisy-btn daisy-btn-outline daisy-btn-block justify-start gap-3"
                  (click)="selectFileSubMode('url-download')"
                >
                  <i class="fas fa-link"></i> Upload from URL
                  <span
                    class="daisy-badge daisy-badge-sm daisy-badge-info ml-auto"
                    >downloads &amp; stores file</span
                  >
                </button>
                <button
                  type="button"
                  class="daisy-btn daisy-btn-outline daisy-btn-block justify-start gap-3"
                  [disabled]="drivePickerLoading"
                  (click)="selectFromGoogleDrive(close)"
                >
                  @if (drivePickerLoading) {
                  <span
                    class="daisy-loading daisy-loading-spinner daisy-loading-sm"
                  ></span>
                  } @else {
                  <i class="fab fa-google-drive text-blue-500"></i>
                  } Google Drive
                </button>
                @if (drivePickerError) {
                <div class="daisy-alert daisy-alert-error text-sm mt-1">
                  {{ drivePickerError }}
                </div>
                }
              </div>
              }

              <!-- Device upload sub-mode -->
              @if (fileSubMode === 'device') {
              <div class="mt-4">
                <button
                  type="button"
                  class="daisy-btn daisy-btn-ghost daisy-btn-sm mb-3"
                  (click)="fileSubMode = null"
                >
                  <i class="fas fa-arrow-left mr-1"></i> Back
                </button>
                <file-pond
                  #myPond
                  [options]="pondOptions"
                  (onaddfile)="pondHandleAddFile($event)"
                  (onremovefile)="pondHandleRemoveFile()"
                >
                </file-pond>
              </div>
              <button
                class="daisy-btn daisy-btn-primary daisy-btn-block mt-2"
                (click)="uploadRecording(close)"
                [disabled]="!currentUploadContext"
              >
                Upload File
              </button>
              }

              <!-- URL download sub-mode -->
              @if (fileSubMode === 'url-download') {
              <form
                [formGroup]="urlUploadForm"
                class="flex flex-col gap-3 mt-2"
              >
                <button
                  type="button"
                  class="daisy-btn daisy-btn-ghost daisy-btn-sm"
                  (click)="fileSubMode = null"
                >
                  <i class="fas fa-arrow-left mr-1"></i> Back
                </button>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium">Content Name</label>
                  <input
                    type="text"
                    formControlName="contentName"
                    placeholder="Name for this resource"
                    class="daisy-input daisy-input-bordered w-full"
                  />
                  @if (urlUploadForm.controls.contentName.invalid &&
                  urlUploadForm.controls.contentName.touched) {
                  <p class="text-error text-sm">Name is required.</p>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium">File URL</label>
                  <input
                    type="url"
                    formControlName="url"
                    placeholder="https://example.com/file.mp4"
                    class="daisy-input daisy-input-bordered w-full"
                  />
                  @if (urlUploadForm.controls.url.invalid &&
                  urlUploadForm.controls.url.touched) {
                  <p class="text-error text-sm">
                    A valid URL starting with http:// or https:// is required.
                  </p>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium">Type</label>
                  <select
                    formControlName="type"
                    class="daisy-select daisy-select-bordered w-full"
                  >
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                    <option value="image">Image</option>
                  </select>
                </div>

                @if (urlSubmitError) {
                <div class="daisy-alert daisy-alert-error text-sm">
                  {{ urlSubmitError }}
                </div>
                }

                <button
                  type="button"
                  class="daisy-btn daisy-btn-primary daisy-btn-block"
                  (click)="submitUrlContent(close)"
                  [disabled]="urlUploadForm.invalid || urlSubmitting"
                >
                  @if (urlSubmitting) {
                  <span
                    class="daisy-loading daisy-loading-spinner daisy-loading-sm"
                  ></span>
                  } @else { Download &amp; Store File }
                </button>
              </form>
              } }

              <!-- Add a URL tab (external URL, stored as-is) -->
              @if (activeTab === 'external-url') {
              <form
                [formGroup]="externalUrlForm"
                class="flex flex-col gap-3 mt-2"
              >
                <p class="text-sm text-base-content/60">
                  The URL will be stored as-is. Clicking the item will open the
                  link directly in a new tab.
                </p>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium">Content Name</label>
                  <input
                    type="text"
                    formControlName="contentName"
                    placeholder="Name for this resource"
                    class="daisy-input daisy-input-bordered w-full"
                  />
                  @if (externalUrlForm.controls.contentName.invalid &&
                  externalUrlForm.controls.contentName.touched) {
                  <p class="text-error text-sm">Name is required.</p>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium">URL</label>
                  <input
                    type="url"
                    formControlName="url"
                    placeholder="https://example.com/resource"
                    class="daisy-input daisy-input-bordered w-full"
                  />
                  @if (externalUrlForm.controls.url.invalid &&
                  externalUrlForm.controls.url.touched) {
                  <p class="text-error text-sm">
                    A valid URL starting with http:// or https:// is required.
                  </p>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium">Type</label>
                  <select
                    formControlName="type"
                    class="daisy-select daisy-select-bordered w-full"
                  >
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                    <option value="image">Image</option>
                  </select>
                </div>

                @if (externalUrlSubmitError) {
                <div class="daisy-alert daisy-alert-error text-sm">
                  {{ externalUrlSubmitError }}
                </div>
                }

                <button
                  type="button"
                  class="daisy-btn daisy-btn-primary daisy-btn-block"
                  (click)="submitExternalUrlContent(close)"
                  [disabled]="externalUrlForm.invalid || externalUrlSubmitting"
                >
                  @if (externalUrlSubmitting) {
                  <span
                    class="daisy-loading daisy-loading-spinner daisy-loading-sm"
                  ></span>
                  } @else { Add URL }
                </button>
              </form>
              }
            </div>
          </div>
        </div>
      </black-ui-modal>
      }
    </div>
    }
    @if (editModeEnabled) {
    <div>
      <div class="daisy-divider"></div>
      <div class="flex justify-end z-100 top-2 right-2 h-fit w-full">
        <black-confirm-dialog
          confirmBtnLevel="danger"
          bodyText="Are you sure you want to proceed? This action is irreversible."
          (confirm)="onRemoveStudyMaterial()"
        >
          <button
            class="daisy-btn daisy-btn-neutral hover:daisy-btn-error daisy-btn-sm hover:cursor-pointer"
            title="Delete"
            *blackConfirmDialogTrigger="let toggle = toggle"
            (click)="toggle()"
          >
            <i class="fas fa-trash"></i> Remove this folder
          </button>
        </black-confirm-dialog>
      </div>
    </div>
    }
  </div>
</div>
